import { useAuthContext } from 'contexts';
import type { RevalidateTrigger } from 'data/FHIRFetcher';
import type { Task, Bundle, BundleEntry } from 'fhir/r4';
import { FHIRProxyClient } from 'fhir-sdoh';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Loader, Message } from 'semantic-ui-react';

import type {
  AssignTaskFormProps,
  AssignTaskOptions,
  DefaultOptionType,
  FlatPractitionerSelectOption,
} from '../types';

import { AssignTaskForm } from './assignTaskForm';
import { ConsultCancelDueDateWarning } from './consultCancelDueDateWarning';
import { ConsultInProgressWarning } from './consultInProgressWarning';
import { useTaskAssignment } from './hooks';
import {
  needsConsultCancelDueDateConfirmBulk,
  needsConsultInProgressConfirmBulk,
  isConsultationTask,
} from './hooks/utils';


interface AssignTaskProps {
  disableMultiSelect?: boolean | undefined;
  flatPractitioners: FlatPractitionerSelectOption[];
  foodVendors: DefaultOptionType[];
  /** Absent on the activity tabs, which are not scoped to one organization. */
  orgCode?: string | undefined;
  practitionerList: DefaultOptionType[];
  render: boolean;
  selectedTasks: Task[];
  setRender: (render: boolean) => void;
  setSelectedTasks: (tasks: Task[]) => void;
  tasksMutate: RevalidateTrigger;
  selectedCareplanTasks: boolean;
}

const AssignTask: React.FC<AssignTaskProps> = ({
  disableMultiSelect = false,
  flatPractitioners,
  foodVendors,
  orgCode,
  practitionerList,
  render,
  selectedTasks,
  setRender,
  setSelectedTasks,
  tasksMutate,
  selectedCareplanTasks,
}) => {
  const [allOptions, setAllOptions] = useState<AssignTaskOptions>({
    assignee: null,
    dueDate: null,
    status: null,
    vendor: null,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showConsultInProgressWarning, setShowConsultInProgressWarning] = useState(false);
  const [showConsultCancelDueDateWarning, setShowConsultCancelDueDateWarning] = useState(false);
  const [pendingConsultMemberEventDate, setPendingConsultMemberEventDate] = useState<string | null>(null);

  const authInfo = useAuthContext();
  const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
  const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

  const selectedNonFoodDeliveryTasks = selectedTasks.some((task) => task?.code?.coding?.[0]?.code !== 'food-delivery');

  const { assignTask, changeStatus, handleChangeDate, changeVendor } = useTaskAssignment({
    flatPractitioners,
    foodVendors,
    // Optional on this component's props because the header is also mounted by the
    // activity tabs; those pass their payer dropdown, which is `'All'` rather than
    // absent, and the member panel passes the member's org. So a mounted assign
    // panel always has one, and `useTaskAssignment` branches on the `'All'` value.
    orgCode: orgCode!,
    tasksMutate,
  });

  const handleOptionChange = (field: keyof AssignTaskOptions, value: string) => {
    setAllOptions({ ...allOptions, [field]: value });
  };

  const runTaskUpdateSubmit = async (consultMemberEventDate?: string | null) => {
    setLoading(true);

    const fhirBundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [],
    };

    let bundleEntry: BundleEntry[] = [];
    const memberEventDateForConsult =
      consultMemberEventDate || pendingConsultMemberEventDate || null;

    // Loop through each task and update the assignee, due date, status, vendor, and create provenance
    for (let i = 0; selectedTasks.length > i; i++) {
      // `selectedTasks[i]` is `Task | undefined` only because of
      // `noUncheckedIndexedAccess`; the loop bound is `selectedTasks.length > i`, so
      // the subscript is always in range. Asserted once here rather than at each of
      // the five reads below — the existing `currentTask!.id` already depended on it.
      let currentTask = selectedTasks[i]!;
      // Asserted once here: `Task.id` is `0..1` in FHIR, but these all came from a
      // search bundle and carry one.
      const currentTaskId = currentTask.id!;

      if (allOptions.vendor) {
        const updateVendorProcess = await changeVendor(allOptions.vendor, currentTask);
        currentTask = updateVendorProcess?.find((resource) => resource?.fullUrl?.includes(currentTaskId))
          ?.resource as Task;

        if (!(allOptions.dueDate || allOptions.status || allOptions.assignee)) {
          bundleEntry = bundleEntry.concat(updateVendorProcess);
        } else {
          const resourcesExceptCurrentTask = updateVendorProcess.filter(
            (resource) => !resource?.fullUrl?.includes(currentTaskId)
          );
          bundleEntry = bundleEntry.concat(resourcesExceptCurrentTask);
        }
      }

      if (allOptions.dueDate) {
        const dueDate = moment(allOptions.dueDate).utc().format('YYYY-MM-DD');
        const updateDueDateProcess = await handleChangeDate(dueDate, currentTask);
        currentTask = updateDueDateProcess?.find((resource) => resource?.fullUrl?.includes(currentTaskId))
          ?.resource as Task;

        // Prevent saving the same task multiple times.
        // If the status and assignee don't need to be updated, we can simply push the FHIR resources returned from the handleChangeDate function.
        // However, if the status or assignee needs to be updated, we must pass the modified current task to ensure those changes are applied.
        if (!(allOptions.status || allOptions.assignee)) {
          bundleEntry = bundleEntry.concat(updateDueDateProcess);
        } else {
          const resourcesExceptCurrentTask = updateDueDateProcess.filter(
            (resource) => !resource?.fullUrl?.includes(currentTaskId)
          );
          bundleEntry = bundleEntry.concat(resourcesExceptCurrentTask);
        }
      }

      if (allOptions.status) {
        // For consult failed/cancelled: apply member event date in changeStatus when bulk dueDate
        // was not already set, so due date + status land in one task write.
        const consultEventDate =
          !allOptions.dueDate &&
          isConsultationTask(currentTask) &&
          ['cancelled', 'failed'].includes(allOptions.status) &&
          memberEventDateForConsult
            ? memberEventDateForConsult
            : undefined;
        const updateStatusProcess = await changeStatus(allOptions.status, currentTask, consultEventDate);
        currentTask = updateStatusProcess?.find((resource) => resource?.fullUrl?.includes(currentTaskId))
          ?.resource as Task;

        // If the assignee doesn't need to be updated, we can directly push the FHIR resources returned from the changeStatus function.
        // Otherwise, we need to extract the updated current task and pass it in to update the assignee accordingly.
        if (!allOptions.assignee) {
          bundleEntry = bundleEntry.concat(updateStatusProcess);
        } else {
          const resourcesExceptCurrentTask = updateStatusProcess.filter(
            (resource) => !resource?.fullUrl?.includes(currentTaskId)
          );
          bundleEntry = bundleEntry.concat(resourcesExceptCurrentTask);
        }
      }

      if (allOptions.assignee) {
        const updatedAssigneeProcess = await assignTask(allOptions.assignee, currentTask);
        bundleEntry = bundleEntry.concat(updatedAssigneeProcess);
      }
    }

    fhirBundle.entry = bundleEntry;

    fhirProxyClient.send('post', '', fhirBundle).then(
      () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
        tasksMutate();
        setRender(!render);
        setLoading(false);
        setAllOptions({
          assignee: null,
          dueDate: null,
          status: null,
          vendor: null,
        });
        setPendingConsultMemberEventDate(null);
        setShowConsultCancelDueDateWarning(false);
      },
      (response: { toJSON: () => { message: string } }) => {
        setErrorMessage(response.toJSON()['message']);
        setLoading(false);
      }
    );

    setSelectedTasks([]);
  };

  const consultInProgressBulkWarningNeeded = needsConsultInProgressConfirmBulk(selectedTasks, allOptions.status);
  const consultCancelDueDateBulkWarningNeeded = needsConsultCancelDueDateConfirmBulk(
    selectedTasks,
    allOptions.status
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (consultInProgressBulkWarningNeeded) {
      setShowConsultInProgressWarning(true);
      return;
    }
    // If bulk already includes a due date, status + due date are written together — no extra prompt.
    if (consultCancelDueDateBulkWarningNeeded && !allOptions.dueDate) {
      setShowConsultCancelDueDateWarning(true);
      return;
    }
    await runTaskUpdateSubmit();
  };

  useEffect(() => {
    if (selectedTasks?.length < 1) {
      setAllOptions({
        assignee: null,
        dueDate: null,
        status: null,
        vendor: null,
      });
      setErrorMessage('');
      setShowConsultInProgressWarning(false);
      setShowConsultCancelDueDateWarning(false);
      setPendingConsultMemberEventDate(null);
    } else {
      // Drop consult-only warnings once the selection no longer includes a consult that needs them
      if (!needsConsultInProgressConfirmBulk(selectedTasks, allOptions.status)) {
        setShowConsultInProgressWarning(false);
      }
      if (!needsConsultCancelDueDateConfirmBulk(selectedTasks, allOptions.status)) {
        setShowConsultCancelDueDateWarning(false);
        setPendingConsultMemberEventDate(null);
      }
    }
    if (selectedCareplanTasks) {
      setErrorMessage('Careplan task(s) have been selected, please remove them to update task owners.');
    } else if (selectedTasks.length > 0 && selectedNonFoodDeliveryTasks) {
      setErrorMessage('Non-food-delivery task(s) have been selected, please remove them to update vendor.');
    } else {
      setErrorMessage('');
    }
  }, [disableMultiSelect, selectedTasks, selectedCareplanTasks, selectedNonFoodDeliveryTasks, allOptions.status]);

  useEffect(() => {
    if (allOptions.status !== 'in-progress') {
      setShowConsultInProgressWarning(false);
    }
    if (allOptions.status !== 'cancelled' && allOptions.status !== 'failed') {
      setShowConsultCancelDueDateWarning(false);
      setPendingConsultMemberEventDate(null);
    }
  }, [allOptions.status]);

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <AssignTaskForm
        allOptions={allOptions}
        disableMultiSelect={disableMultiSelect}
        foodVendors={foodVendors}
        // Cast at the boundary, not adapted: the same two functions are installed as
        // before, against wider React/Semantic prop types.
        onSubmit={handleSubmit as unknown as AssignTaskFormProps['onSubmit']}
        onOptionChange={handleOptionChange as AssignTaskFormProps['onOptionChange']}
        practitionerList={practitionerList}
        selectedCareplanTasks={selectedCareplanTasks}
        selectedNonFoodDeliveryTasks={selectedNonFoodDeliveryTasks}
      />
      <ConsultInProgressWarning
        open={showConsultInProgressWarning && consultInProgressBulkWarningNeeded}
        onDismiss={() => setShowConsultInProgressWarning(false)}
        onConfirm={() => {
          setShowConsultInProgressWarning(false);
          void runTaskUpdateSubmit();
        }}
      />
      <ConsultCancelDueDateWarning
        open={showConsultCancelDueDateWarning && consultCancelDueDateBulkWarningNeeded && !allOptions.dueDate}
        targetStatus={allOptions.status as Task['status'] | null}
        onDismiss={() => {
          setShowConsultCancelDueDateWarning(false);
          setPendingConsultMemberEventDate(null);
        }}
        onConfirm={(memberEventDate) => {
          setPendingConsultMemberEventDate(memberEventDate);
          setShowConsultCancelDueDateWarning(false);
          void runTaskUpdateSubmit(memberEventDate);
        }}
      />
      {errorMessage && <Message size="small" content={errorMessage} negative onDismiss={() => setErrorMessage('')} />}
    </>
  );
};

export default AssignTask;
